import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const STORAGE_KEY = "ao_popup_v1";

export function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setOpen(true);
    };

    // Desktop: mouse sai pelo topo (barra do navegador)
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY < 20) trigger();
    };

    // Mobile/fallback: 45 segundos de permanência
    const timer = setTimeout(trigger, 45000);

    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "dismissed");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Informe um e-mail válido.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .upsert({ email: email.trim().toLowerCase() }, { onConflict: "email", ignoreDuplicates: true });
      if (error) throw error;
      setDone(true);
      localStorage.setItem(STORAGE_KEY, "subscribed");
      setTimeout(() => setOpen(false), 2500);
    } catch {
      toast.error("Erro ao cadastrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="popup-title">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismiss} aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="bg-gradient-to-br from-[#1A3870] to-[#0f2454] p-6 text-white text-center">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 h-7 w-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
          <div className="text-4xl mb-2">🎁</div>
          <h2 id="popup-title" className="text-xl font-bold tracking-tight mb-1">
            Antes de ir embora...
          </h2>
          <p className="text-blue-200 text-sm">
            Cadastre seu e-mail e receba ofertas exclusivas de atacado direto na sua caixa de entrada.
          </p>
        </div>

        <div className="p-6">
          {done ? (
            <div className="text-center py-4">
              <div className="text-3xl mb-3">✅</div>
              <p className="font-semibold text-[#1A3870] text-lg">Cadastrado com sucesso!</p>
              <p className="text-sm text-muted-foreground mt-1">Fique de olho na sua caixa de entrada.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                inputMode="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                aria-label="Seu e-mail"
                required
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#F07230] hover:bg-[#d95f20] text-white font-bold rounded-lg"
              >
                {loading ? "Cadastrando..." : "Quero receber as ofertas"}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                Sem spam. Cancele quando quiser.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
